import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAtividadeDoMes, useMesesComAtividade } from '../../api/queries/repositorios'
import { Abas } from '../../components/Abas'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../components/Estados'
import { Explicacao } from '../../components/Explicacao'
import { dataSimples } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import type { FiltrosDeCommits, SetorDoAutor } from '../../types/repositorios'
import { AtividadePorDia } from './AtividadePorDia'
import { AvisoDaEquipeInterna } from './AvisoDaEquipeInterna'
import { ListaDeAutores } from './ListaDeAutores'
import { ListaDeCommits } from './ListaDeCommits'
import { NumerosDoMes } from './NumerosDoMes'
import { OQueFoiModificado } from './OQueFoiModificado'
import { RepositoriosConhecidos } from './RepositoriosConhecidos'
import { ResumoDaIa } from './ResumoDaIa'
import { SeletorDeMes } from './SeletorDeMes'
import { SituacaoDaColeta } from './SituacaoDaColeta'
import { SolicitacoesCitadas } from './SolicitacoesCitadas'
import { TabelaPorPessoa } from './TabelaPorPessoa'
import { TabelaPorRepositorio } from './TabelaPorRepositorio'
import { TabelaPorSetor } from './TabelaPorSetor'
import { competenciaAtual, competenciaValida } from './setores'

const ABAS = ['visao', 'pessoas', 'commits', 'repositorios'] as const

type Aba = (typeof ABAS)[number]

/**
 * A atividade nos repositórios do e-Cidade — a tela que o dono pediu a partir do Diário de Bordo.
 *
 * **Tudo vive na URL**: mês, aba e filtros. `/repositorios?competencia=2026-09&aba=pessoas` é um
 * endereço que vai por e-mail para o comitê, e recarregar não pode devolver a pessoa a outro mês.
 *
 * **O mês corrente termina ontem.** O servidor decide (D-1) e a tela só escreve o período que ele
 * devolveu — o texto do resumo e os números saem do mesmo recorte.
 *
 * **A equipe interna está fora de tudo por padrão** (decisão do dono, 07/09/2026): o servidor
 * exclui quem está no Diário de Bordo; a tela só avisa quanto ficou de fora e oferece o recorte
 * `equipeInterna=true` na aba de commits para quem quiser ver.
 *
 * @returns a tela
 */
export function PaginaAtividadeDeRepositorios() {
  const t = TEXTOS.repositorios
  const [parametros, definirParametros] = useSearchParams()
  const pedido = parametros.get('competencia') ?? ''
  const competencia = competenciaValida(pedido) ? pedido : competenciaAtual()
  const aba: Aba = (ABAS as readonly string[]).includes(parametros.get('aba') ?? '') ? (parametros.get('aba') as Aba) : 'visao'
  const meses = useMesesComAtividade()
  const atividade = useAtividadeDoMes(competencia)

  const trocar = useCallback(
    (mudancas: Record<string, string | undefined>) => {
      const proximos = new URLSearchParams(parametros)
      for (const [chave, valor] of Object.entries(mudancas)) {
        if (valor === undefined || valor === '') {
          proximos.delete(chave)
        } else {
          proximos.set(chave, valor)
        }
      }
      definirParametros(proximos)
    },
    [parametros, definirParametros],
  )

  const filtros: FiltrosDeCommits = {
    setor: (parametros.get('setor') as SetorDoAutor | null) ?? undefined,
    autorId: numero(parametros.get('autorId')),
    repositorioId: numero(parametros.get('repositorioId')),
    semSolicitacao: booleano(parametros.get('semSolicitacao')),
    integracao: booleano(parametros.get('integracao')),
    busca: parametros.get('busca') ?? undefined,
    incluirHoje: parametros.get('incluirHoje') === 'true' || undefined,
    equipeInterna: parametros.get('equipeInterna') === 'true' || undefined,
    pagina: numero(parametros.get('pagina')) ?? 0,
  }

  function mudarFiltros(novos: FiltrosDeCommits) {
    trocar({
      setor: novos.setor,
      autorId: texto(novos.autorId),
      repositorioId: texto(novos.repositorioId),
      semSolicitacao: texto(novos.semSolicitacao),
      integracao: texto(novos.integracao),
      busca: novos.busca,
      incluirHoje: novos.incluirHoje ? 'true' : undefined,
      equipeInterna: novos.equipeInterna ? 'true' : undefined,
      pagina: novos.pagina ? String(novos.pagina) : undefined,
    })
  }

  const dados = atividade.data?.atividade
  const periodo = dados
    ? dados.vazio
      ? t.mesComecouHoje
      : dados.mesFechado
        ? t.mesFechado(dataSimples(dados.inicio), dataSimples(dados.ate))
        : t.ateOntem(dataSimples(dados.ate))
    : null

  return (
    <div className="space-y-5">
      <header>
        <h1 className="titulo-da-pagina flex items-center gap-2">
          {t.titulo}
          <Explicacao termo="atividadeNosRepositorios" />
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{t.ajuda}</p>
      </header>

      <div className="cartao flex flex-wrap items-end justify-between gap-3 p-4">
        <SeletorDeMes competencia={competencia} meses={meses.data ?? []} aoEscolher={(mes) => trocar({ competencia: mes, pagina: undefined })} />
        {periodo && <p className="text-sm text-slate-600">{periodo}</p>}
      </div>

      {atividade.isPending && <Esqueleto linhas={5} />}
      {atividade.isError && <AvisoDeErro erro={atividade.error} />}
      {meses.data && meses.data.length === 0 && !atividade.isPending && (
        <EstadoVazio titulo={t.semMeses.titulo} ajuda={t.semMeses.ajuda} />
      )}

      {atividade.data && dados && (
        <Abas
          abas={[
            { id: 'visao', rotulo: t.abas.visao },
            { id: 'pessoas', rotulo: t.abas.pessoas, contador: dados.porPessoa.length || undefined },
            { id: 'commits', rotulo: t.abas.commits, contador: dados.numeros ? dados.numeros.commits + dados.numeros.integracoes : undefined },
            { id: 'repositorios', rotulo: t.abas.repositorios },
          ]}
          ativa={aba}
          aoMudar={(id) => trocar({ aba: id })}
          rotulo={t.titulo}
        >
          <div className="mt-4 space-y-4">
            {aba === 'visao' && (
              <>
                <ResumoDaIa apoio={atividade.data.resumo} competencia={competencia} />
                {dados.numeros && <NumerosDoMes numeros={dados.numeros} />}
                {dados.numeros && <AvisoDaEquipeInterna numeros={dados.numeros} competencia={competencia} />}
                <TabelaPorSetor linhas={dados.porSetor} />
                {!dados.vazio && <OQueFoiModificado dados={dados} />}
                <AtividadePorDia linhas={dados.porDia} />
                <SolicitacoesCitadas solicitacoes={dados.solicitacoesCitadas} />
              </>
            )}
            {aba === 'pessoas' && <TabelaPorPessoa linhas={dados.porPessoa} competencia={competencia} />}
            {aba === 'commits' && (
              <ListaDeCommits competencia={competencia} mesFechado={dados.mesFechado} filtros={filtros} aoMudarFiltros={mudarFiltros} />
            )}
            {aba === 'repositorios' && (
              <>
                <TabelaPorRepositorio linhas={dados.porRepositorio} />
                <SituacaoDaColeta />
                <RepositoriosConhecidos />
                <ListaDeAutores />
              </>
            )}
          </div>
        </Abas>
      )}
    </div>
  )
}

function numero(valor: string | null): number | undefined {
  const n = valor == null ? NaN : Number(valor)
  return Number.isFinite(n) ? n : undefined
}

function booleano(valor: string | null): boolean | undefined {
  return valor === 'true' ? true : valor === 'false' ? false : undefined
}

function texto(valor: number | boolean | undefined): string | undefined {
  return valor === undefined ? undefined : String(valor)
}
